import React, { useEffect, useReducer, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { create } from "@/actions/sliders.action";
import { useNavigate } from "react-router-dom";
import { url } from "@/utils";
import { getAll } from "@/actions/image.actions";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AddItemModal from "./AddItemModel";

// ✅ Initial State
const initialState = {
  title: "",
  description: "",
  interval: 8000,
  transparency: 30,
  items: [],
  item: {
    title: "",
    description: "",
    image: "",
    order: 0,
  },
};

// ✅ State and Action Types
type State = {
  title: string;
  description: string;
  interval: number;
  transparency: number;
  items: { title: string; description: string; image: string; order: number }[];
  item: { title: string; description: string; image: string; order: number };
};

type Action =
  | { type: "SET_FIELD"; field: string; value: any }
  | { type: "SET_ITEM_FIELD"; field: string; value: any }
  | { type: "ADD_ITEM" }
  | { type: "REMOVE_ITEM"; index: number }
  | { type: "RESET" };

// ✅ Reducer Function
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value };
    case "SET_ITEM_FIELD":
      return {
        ...state,
        item: { ...state.item, [action.field]: action.value },
      };
    case "ADD_ITEM":
      return {
        ...state,
        items: [...state.items, state.item],
        item: initialState.item, // Reset the item state
      };
    case "REMOVE_ITEM":
      return {
        ...state,
        items: state.items.filter((_, i) => i !== action.index),
      };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

export default function CreateSlider() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [state, localDispatch] = useReducer(reducer, initialState);
  const [isModalOpen, setModalOpen] = useState(false);

  const { images, status: imageStatus } = useSelector(
    (state: any) => state.images
  );
  const { status } = useSelector((state: any) => state.sliders);

  useEffect(() => {
    if (imageStatus === "idle") {
      dispatch(getAll() as any);
    }
  }, [imageStatus, dispatch]);

  const handleChange = useCallback(
    (field: string, value: any) => {
      localDispatch({ type: "SET_FIELD", field, value });
    },
    [localDispatch]
  );

  const handleItemChange = useCallback(
    (field: string, value: any) => {
      localDispatch({ type: "SET_ITEM_FIELD", field, value });
    },
    [localDispatch]
  );

  const addItem = () => {
    if (!state.item.title || !state.item.image) return;
    localDispatch({ type: "ADD_ITEM" });
    setModalOpen(false);
  };

  const removeItem = (index: number) => {
    localDispatch({ type: "REMOVE_ITEM", index });
  };

  const handleSubmit = () => {
    const slider = {
      title: state.title,
      description: state.description,
      interval: state.interval,
      transparency: state.transparency,
    };

    dispatch(create(slider) as any);

    if (status === "success") {
      navigate(url("Admin.Sliders"));
    }
  };

  // ✅ Map image from state to actual image object
  const getImageData = (imageId: string) => {
    return images.find((img) => img._id === imageId);
  };

  return (
    <div className="bg-white rounded shadow w-full p-4">
      <Box sx={{ width: "100%" }}>
        <Typography variant="h6">Create New Slider</Typography>

        {/* ✅ Main Slider Fields */}
        <TextField
          label="Title"
          fullWidth
          margin="normal"
          value={state.title}
          onChange={(e) => handleChange("title", e.target.value)}
        />

        <TextField
          label="Description"
          fullWidth
          margin="normal"
          value={state.description}
          onChange={(e) => handleChange("description", e.target.value)}
        />

        <TextField
          label="Interval (ms)"
          fullWidth
          margin="normal"
          type="number"
          value={state.interval}
          onChange={(e) => handleChange("interval", Number(e.target.value))}
        />

        <TextField
          label="Transparency (%)"
          fullWidth
          margin="normal"
          type="number"
          value={state.transparency}
          onChange={(e) => handleChange("transparency", Number(e.target.value))}
        />

        {/* ✅ Display Added Items */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {state.items.map((item, index) => {
            const image = getImageData(item.image);
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card>
                  <CardContent>
                    {image && (
                      <CardMedia
                        component="img"
                        height="100"
                        image={image.url}
                        alt={image.altText}
                        sx={{
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    )}
                    <Typography>{item.title}</Typography>
                    <Typography>{item.description}</Typography>
                  </CardContent>
                  <CardActions>
                    <Button color="error" onClick={() => removeItem(index)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* ✅ Open Modal */}
        <Button
          onClick={() => setModalOpen(true)}
          sx={{ mt: 2 }}
          variant="outlined"
        >
          Add Item
        </Button>

        {/* ✅ Use Modal for Adding New Items */}
        <AddItemModal
          open={isModalOpen}
          onClose={() => setModalOpen(false)}
          item={state.item}
          handleItemChange={handleItemChange}
          images={images}
          addItem={addItem}
        />

        {/* ✅ Save Button */}
        <Button
          onClick={handleSubmit}
          sx={{ mt: 2 }}
          variant="contained"
          color="primary"
          fullWidth
        >
          Save
        </Button>
      </Box>
    </div>
  );
}
